const API_URL = process.env.REACT_APP_ANALYTICS_API_URL || '/api/analytics';

const STORAGE_VISIT = 'ca_analytics_visit_sent';
const STORAGE_SID = 'ca_analytics_sid';

function postJson(payload) {
  return fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

function postBeacon(payload) {
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (navigator.sendBeacon(API_URL, blob)) return Promise.resolve(true);
    } catch {
      /* fall through */
    }
  }
  return postJson(payload);
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(STORAGE_SID);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(STORAGE_SID, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Tracks section dwell via IntersectionObserver; sends visit + session_end to `/api/analytics`.
 * @returns {() => void} cleanup (ends session once)
 */
export function startVisitAnalytics() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const sessionId = getSessionId();
  const dwell = {};
  const visibleSince = {};
  const totalStart = performance.now();
  let ended = false;

  const obs = new IntersectionObserver(
    (entries) => {
      const now = performance.now();
      for (const en of entries) {
        const key = en.target.getAttribute('data-analytics-section');
        if (!key) continue;
        if (en.isIntersecting && en.intersectionRatio >= 0.35) {
          if (visibleSince[key] == null) visibleSince[key] = now;
        } else if (visibleSince[key] != null) {
          dwell[key] = (dwell[key] || 0) + (now - visibleSince[key]);
          visibleSince[key] = null;
        }
      }
    },
    { threshold: [0, 0.35, 0.55] }
  );

  document.querySelectorAll('[data-analytics-section]').forEach((el) => obs.observe(el));

  function flushVisit() {
    try {
      if (sessionStorage.getItem(STORAGE_VISIT)) return;
      sessionStorage.setItem(STORAGE_VISIT, '1');
    } catch {
      return;
    }

    postJson({
      type: 'visit',
      sessionId,
      path: window.location.pathname + window.location.search,
      referrer: document.referrer || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ua: navigator.userAgent,
    });
  }

  flushVisit();

  function endSession() {
    if (ended) return;
    ended = true;
    obs.disconnect();

    const now = performance.now();
    for (const key of Object.keys(visibleSince)) {
      const t0 = visibleSince[key];
      if (t0 != null) dwell[key] = (dwell[key] || 0) + (now - t0);
      visibleSince[key] = null;
    }

    const totalMs = Math.round(now - totalStart);
    const clean = {};
    for (const [k, v] of Object.entries(dwell)) {
      clean[k] = Math.round(v);
    }

    postBeacon({
      type: 'session_end',
      sessionId,
      totalMs,
      dwell: clean,
    });
  }

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') endSession();
  };

  window.addEventListener('pagehide', endSession);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('pagehide', endSession);
    document.removeEventListener('visibilitychange', onVisibility);
    endSession();
  };
}
